import { PromptForm } from '@/chat/components/PromptForm'
import { PromptFormData } from '@/chat/components/PromptForm/types'
import { throwOnAuthError } from '@/config/queryClient'
import { ApiError } from '@/services/api'
import { usePlaybookCreation } from '@/shared/hooks/data-hooks/playbooks/usePlaybookCreation'
import { useWorkspace } from '@/shared/hooks/data-hooks/workspaces/useWorkspace'
import { useToast } from '@/shared/hooks/useToast'
import { useWorkspaceContext } from '@/shared/hooks/useWorkspaceContext'
import {
  clearLastPlaybookQuery,
  getCurrentLastPlaybookQuery,
  setLastPlaybookQuery,
} from '@/shared/utils/lastPlaybookQuery'
import { setPlaybookFirstMessage } from '@/shared/utils/playbookFirstMessage'
import { isFreePlan } from '@/shared/utils/subscriptionPlan'
import { ApiPlaybookCreationResponse } from '@/types/playbook'
import React, { forwardRef, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

type PromptFormProps = React.ComponentProps<typeof PromptForm>
type PlaybookCreationFormProps = Omit<PromptFormProps, 'onSubmit'> & {
  onSubmit?: (
    playbook: ApiPlaybookCreationResponse,
    formData: { query: string; files: File[]; isPublic?: boolean }
  ) => Promise<void> | void
}

export const PlaybookCreationForm = forwardRef<
  HTMLTextAreaElement,
  PlaybookCreationFormProps
>(function PlaybookCreationForm({ onSubmit, ...props }, ref) {
  const [savedQuery, setSavedQuery] = useState<string>('')
  const navigate = useNavigate()
  const { toast } = useToast()

  const { workspaceId } = useWorkspaceContext()
  const { data: { data: workspace } = {} } = useWorkspace(workspaceId)

  const { mutateAsync: createPlaybook } = usePlaybookCreation({
    throwOnError: throwOnAuthError,
  })

  useEffect(() => {
    const saved = getCurrentLastPlaybookQuery()
    if (saved) {
      setSavedQuery(saved)
    }
  }, [])

  const handlePromptSubmit = async (data: PromptFormData) => {
    try {
      const { data: playbook } = await createPlaybook({
        description: data.query,
        isPublic: data.visibility === 'public',
      })

      setPlaybookFirstMessage(playbook._id, data.query)

      if (onSubmit) {
        await onSubmit(playbook, {
          query: data.query,
          files: data.files,
          isPublic: data.visibility === 'public',
        })
      }

      clearLastPlaybookQuery()
      setSavedQuery('')

      navigate(`/playbooks/${playbook._id}?new=true`)
    } catch (error) {
      if (error instanceof ApiError) {
        setLastPlaybookQuery(data.query)
        setSavedQuery(data.query)
      }
      toast({
        variant: 'destructive',
        title: 'Failed to create playbook',
        description: 'An unexpected error occurred. Please try again.',
      })
    }
  }

  return (
    <PromptForm
      {...props}
      ref={ref}
      onSubmit={handlePromptSubmit}
      value={{
        query: savedQuery,
        visibility: isFreePlan(workspace?.subscription?.subscriptionPlan?.type)
          ? 'public'
          : 'private',
      }}
      showExamples
    />
  )
})
